import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  BarChart2,
  Sparkles,
  Network,
  LifeBuoy,
  MonitorPlay,
  ChevronUp
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import useComparisonStore from "../ComparisonModals/store/comparisonStore";

/**
 * QuickNavBar - Apple-inspired navigation component
 * 
 * Features:
 * 1. No button shapes or borders
 * 2. Pure text and icon navigation
 * 3. Premium glass backdrop
 * 4. 3D lifted text effect for Coverage button
 */
const QuickNavBar = ({
  fixed = false,
  isVisible = true,
  openCoverageSidebar,
  openComparisonModal,
}) => {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  // No longer needed since we're using direct motion props
  
  // Handle scroll to top functionality
  const handleScrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };
  
  // Close mobile menu when window resizes
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768 && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };
    
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isMobileMenuOpen]);

  // Define the navigation actions
  const navActions = [
    {
      label: "Compare",
      icon: <BarChart2 size={20} className="text-blue-500" />,
      description: "Compare networks",
      to: "#",
      onClick: (e) => {
        e.preventDefault();
        if (openComparisonModal && typeof openComparisonModal === 'function') {
          openComparisonModal();
          setIsMobileMenuOpen(false);
        }
      }
    },
    {
      label: "Recommend",
      icon: <Sparkles size={20} className="text-purple-500" />,
      description: "Get recommendations",
      to: "#",
      onClick: (e) => {
        e.preventDefault();
        
        console.log("QuickNavBar: Recommend button clicked");
        
        // Get only the functions we need
        const { closeAllModals, openRecommendationModal } = useComparisonStore.getState();
        
        // First close all modals to ensure a clean state
        closeAllModals();
        console.log("QuickNavBar: All modals closed");
        
        // Set a flag to prevent other modals from opening
        localStorage.setItem('recommendationJustOpened', 'true');
        console.log("QuickNavBar: Set recommendationJustOpened flag");
        
        // Add a small delay to ensure all modals are fully closed
        setTimeout(() => {
          // Then open ONLY the recommendation modal with our isolated function
          openRecommendationModal();
          console.log("Opening ONLY recommendation modal from QuickNavBar");
          
          // Get current state to verify
          const { isRecommendationModalOpen, isSelectionModalOpen } = useComparisonStore.getState();
          console.log("QuickNavBar: Modal states after opening recommendation:", {
            isRecommendationModalOpen,
            isSelectionModalOpen
          });
          
          // Double-check that the recommendation modal is actually open after a short delay
          setTimeout(() => {
            const { isRecommendationModalOpen } = useComparisonStore.getState();
            if (!isRecommendationModalOpen) {
              console.log("QuickNavBar: Recommendation modal was closed prematurely, reopening");
              openRecommendationModal();
            } else {
              console.log("QuickNavBar: Recommendation modal is still open as expected");
            }
          }, 300);
        }, 100);
        
        setIsMobileMenuOpen(false);
      }
    },
    {
      label: "WonderWatch",
      icon: <MonitorPlay size={20} className="text-blue-600" />,
      description: "Network status",
      to: "/wonderwatch",
      featured: true,
      onClick: () => setIsMobileMenuOpen(false)
    },
    {
      label: "Wireless Advisor",
      icon: <Network size={20} className="text-teal-500" />,
      description: "Optimize your Wi-Fi",
      to: "/wireless-wonder",
      onClick: () => setIsMobileMenuOpen(false)
    },
    {
      label: "Support",
      icon: <LifeBuoy size={20} className="text-rose-500" />,
      description: "Get help",
      to: "/contact-us",
      onClick: () => setIsMobileMenuOpen(false)
    },
  ];
  
  // Determine container element
  const Container = fixed ? motion.div : 'div';
  
  // Apple-inspired glass effect styles - completely borderless
  const baseStyles = {
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(25px) saturate(180%)',
    WebkitBackdropFilter: 'blur(25px) saturate(180%)',
    zIndex: fixed ? 50 : 40,
  };
  
  // Position styles based on fixed prop
  const positionStyles = fixed
    ? {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
      }
    : {
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: '-32px',
      };
  
  // Refined animation variants
  const containerVariants = {
    hidden: { 
      y: -100, 
      opacity: 0,
      transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] }
    },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] }
    },
    exit: {
      y: -100, 
      opacity: 0,
      transition: { duration: 0.3, ease: [0.16, 1, 0.3, 1] }
    }
  };

  // No longer needed as we're using direct motion props

  return (
    <AnimatePresence>
      {(!fixed || (fixed && isVisible)) && (
        <Container
          className="w-full mx-auto"
          style={{ ...baseStyles, ...positionStyles }}
          variants={fixed ? containerVariants : {}}
          initial={fixed ? "hidden" : false}
          animate={fixed ? "visible" : undefined}
          exit={fixed ? "exit" : undefined}
        >
          <div className="relative w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
            <div className="flex flex-row items-center justify-between px-4 py-2">
              {/* Logo */}
              <Link 
                to="/" 
                className="flex-shrink-0"
                aria-label="Go to homepage"
              >
                <img
                  src="https://apextech.group/wondernet-logo-white.png"
                  alt="Wondernet Logo"
                  className="h-7 w-auto"
                />
              </Link>
              
              {/* Mobile Menu Toggle - Text only */}
              <button 
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-expanded={isMobileMenuOpen}
                aria-controls="mobile-menu"
                aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
                className="md:hidden text-gray-800"
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className="h-5 w-5" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  {isMobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
              
              {/* Desktop Navigation Links - Pure text and icons */}
              <div className="hidden md:flex items-center space-x-8">
                {navActions.map((action, index) => (
                  <Link
                    key={action.label}
                    to={action.to}
                    onClick={action.onClick}
                    onMouseEnter={() => setHoveredItem(index)}
                    onMouseLeave={() => setHoveredItem(null)}
                    className="flex items-center space-x-1.5 text-gray-800 hover:text-blue-600 transition-colors"
                    aria-label={`${action.label}: ${action.description}`}
                  >
                    {action.icon}
                    <span 
                      className="text-sm font-medium"
                      style={{ 
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                        letterSpacing: '-0.01em',
                      }}
                    >
                      {action.label}
                    </span>
                  </Link>
                ))}
                
                {/* Coverage Button - Apple-style button with 3D text effect */}
                <motion.button
                  onClick={() => {
                    openCoverageSidebar();
                    setIsMobileMenuOpen(false);
                  }}
                  className="px-5 py-1.5 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 focus:outline-none"
                  aria-label="View coverage options"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  style={{
                    boxShadow: "0 2px 8px rgba(37, 99, 235, 0.3)"
                  }}
                >
                  <span
                    className="text-sm font-semibold text-white"
                    style={{
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      letterSpacing: '-0.01em',
                      textShadow: "0 1px 1px rgba(0, 0, 0, 0.1)",
                    }}
                  >
                    Coverage
                  </span>
                </motion.button>
                
                {/* Scroll to Top - Icon only */}
                <button
                  onClick={handleScrollToTop}
                  className="text-gray-800 hover:text-blue-600 transition-colors"
                  aria-label="Scroll to top"
                >
                  <ChevronUp size={18} />
                </button>
              </div>
              
              {/* Mobile Navigation Menu - Text only */}
              <AnimatePresence>
                {isMobileMenuOpen && (
                  <motion.div
                    id="mobile-menu"
                    className="absolute top-full right-4 left-4 mt-2 p-4 rounded-2xl z-50"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      backdropFilter: 'blur(30px) saturate(180%)',
                      WebkitBackdropFilter: 'blur(30px) saturate(180%)'
                    }}
                  >
                    <div className="flex flex-col space-y-4">
                      {navActions.map((action) => (
                        <Link
                          key={`mobile-${action.label}`}
                          to={action.to}
                          onClick={action.onClick}
                          className="flex items-center space-x-3 text-gray-800 hover:text-blue-600 transition-colors"
                          aria-label={`${action.label}: ${action.description}`}
                        >
                          {action.icon}
                          <div>
                            <div 
                              className="font-medium"
                              style={{ 
                                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                letterSpacing: '-0.01em',
                              }}
                            >
                              {action.label}
                            </div>
                            <div className="text-xs text-gray-500">
                              {action.description}
                            </div>
                          </div>
                        </Link>
                      ))}
                      
                      {/* Mobile Coverage Button - Apple-style button */}
                      <motion.button
                        onClick={() => {
                          openCoverageSidebar();
                          setIsMobileMenuOpen(false);
                        }}
                        className="flex items-center space-x-3 text-left focus:outline-none"
                        aria-label="View coverage options"
                        whileHover={{ x: 4 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <div className="p-2 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                          </svg>
                        </div>
                        <div>
                          <div
                            className="font-semibold text-blue-600"
                            style={{
                              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                              letterSpacing: '-0.01em',
                              textShadow: "0px 1px 2px rgba(37, 99, 235, 0.2)"
                            }}
                          >
                            Coverage
                          </div>
                          <div className="text-xs text-gray-500">
                            View coverage options
                          </div>
                        </div>
                      </motion.button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </Container>
      )}
    </AnimatePresence>
  );
};

export default QuickNavBar;